import { useEffect, useSyncExternalStore } from "react";
import { ApiError, api, setUnauthenticatedHandler, type AuthUser } from "../api";

// Auth-Zustand der gesamten Oberfläche (docs/auth.md). Eine Session, also ein
// Zustand: ein Modul-Store statt eines Contexts — kein Provider zum Einfädeln,
// und `lib/api` kann den Session-Verlust ohne React-Umweg melden.
//
// Geladen wird genau EINMAL beim Start (`GET /api/auth/me`). Kein Polling: die
// Session läuft nicht heimlich ab, sondern fällt beim nächsten API-Aufruf als
// 401 auf — den meldet der Transport hierher.

/** Pflicht-Client-Rolle aus Keycloak; ohne sie bleibt die Oberfläche gesperrt. */
const REQUIRED_ROLE = "access";

export type AuthState =
  | { status: "loading" }
  /** Keine Session — Login-Ansicht. */
  | { status: "anonymous" }
  /** Angemeldet, aber ohne Rolle `access`. NIEMALS zum Login schicken (Schleife). */
  | { status: "forbidden"; user: AuthUser | null }
  | { status: "authenticated"; user: AuthUser };

let state: AuthState = { status: "loading" };
const listeners = new Set<() => void>();

function setState(next: AuthState): void {
  state = next;
  listeners.forEach((notify) => notify());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

let started = false;

/** Einmaliger Start-Load; weitere Aufrufe (StrictMode, zweiter Hook) prallen ab. */
function loadCurrentUser(): void {
  if (started) return;
  started = true;
  api
    .getCurrentUser()
    .then((user) => {
      setState(
        user.roles.includes(REQUIRED_ROLE)
          ? { status: "authenticated", user }
          : { status: "forbidden", user },
      );
    })
    .catch((e: unknown) => {
      // 403 = angemeldet ohne Rolle. Alles andere (401, Netzfehler, 5xx) führt
      // fail-closed in die Login-Ansicht.
      const forbidden = e instanceof ApiError && e.status === 403;
      setState(forbidden ? { status: "forbidden", user: null } : { status: "anonymous" });
    });
}

// Session im laufenden Betrieb verloren: kein Redirect mitten im fetch, nur ein
// Zustandswechsel — laufende Eingaben bleiben im DOM stehen.
setUnauthenticatedHandler(() => {
  if (state.status !== "anonymous") setState({ status: "anonymous" });
});

/** Aktueller Auth-Zustand; stößt beim ersten Einsatz den Start-Load an. */
export function useAuth(): AuthState {
  const snapshot = useSyncExternalStore(subscribe, () => state);
  useEffect(loadCurrentUser, []);
  return snapshot;
}

/** Anzeigename für die Kopfzeile — Name, sonst E-Mail, sonst die Kennung. */
export function displayName(user: AuthUser): string {
  return user.name ?? user.email ?? user.sub;
}

/**
 * Abmelden: Session serverseitig verwerfen, dann zur RP-initiated-Abmeldung bei
 * Keycloak navigieren (echte Navigation, kein fetch).
 */
export async function signOut(): Promise<void> {
  try {
    const { logoutUrl } = await api.logout();
    if (logoutUrl) {
      window.location.href = logoutUrl;
      return;
    }
  } catch {
    // Session war schon weg oder der Server ist nicht erreichbar — lokal
    // abmelden ist hier die sichere Richtung.
  }
  setState({ status: "anonymous" });
}
